import React, { useState, useEffect } from 'react';
import { sessionsApi } from '../../api/sessions';
import SessionCard from './SessionCard';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import ErrorMessage from '../Common/ErrorMessage';
import NewSessionDialog from '../Session/NewSessionDialog';
import './Sessions.css';

const SessionList = ({ storyId, projectId, onSelect, activeSessionId }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);

  const loadSessions = async () => {
    if (!storyId) {
      setSessions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await sessionsApi.list(storyId);
      setSessions(data);
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionCreated = (newSession) => {
    setSessions(prev => [newSession, ...prev]);
    onSelect(newSession.id);
  };

  useEffect(() => {
    loadSessions();
  }, [storyId]);

  if (isLoading) {
    return <Loading text="Loading sessions..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadSessions} />;
  }

  return (
    <div className="session-list">
      <div className="session-list__header">
        <h2 className="session-list__title">Sessions</h2>
        <Button
          variant="primary"
          size="small"
          onClick={() => setShowNewSessionModal(true)}
        >
          New Session
        </Button>
      </div>

      {sessions.length === 0 ? (
        <div className="session-list__empty">
          <p>No sessions yet.</p>
          <p className="session-list__empty-hint">Create your first session to start chatting!</p>
        </div>
      ) : (
        <div className="session-list__items">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onClick={() => onSelect(session.id)}
              isActive={session.id === activeSessionId}
              onRename={(id, newName) => {
                setSessions(sessions.map(s =>
                  s.id === id ? { ...s, name: newName } : s
                ));
              }}
            />
          ))}
        </div>
      )}

      {showNewSessionModal && storyId && (
        <NewSessionDialog
          storyId={storyId}
          onClose={() => setShowNewSessionModal(false)}
          onSessionCreated={handleSessionCreated}
        />
      )}
    </div>
  );
};

export default SessionList;
